import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  ErrorState,
  FilterField,
  FilterPanel,
  LoadingState,
  Select,
  Text,
} from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import { Printer } from "lucide-react";
import { StartListSheet } from "../../../components/StartListSheet";
import { startListBlocks, useStartList } from "../../../lib/queries/startlists";
import { useMyTeams } from "../../../lib/queries/teams";
import {
  EXPORT_FLAG_LABELS,
  EXPORT_FLAG_ORDER,
  EXPORT_SCOPE_LABELS,
  exportSearchParams,
  parseExportOptions,
  rowFilterQuery,
  type ExportOptions,
  type ExportScope,
} from "../../../lib/startListExport";
import styles from "./StartListViewPage.module.css";
import type { ID } from "../../../lib/id";

/**
 * The published running order, read-only: the club's view, the printable
 * one and (phase 2) the downloads, which are all the same document.
 *
 * It renders OUTSIDE the app shell, and that is the point. Printing has
 * to lose the sidebar, the topbar and every button, and the only way to
 * do that from inside the shell would be a stylesheet reaching into
 * `.adminer-*` classes — which tokens.css is explicitly forbidden from
 * doing. With no chrome on the page there is nothing to hide.
 *
 * The options (lib/startListExport.ts) live in the URL, so a cut of the
 * list — „Luckies, jen jejich kategorie, bez ČP" — is a link somebody can
 * be sent. A club sees the WHOLE list by default: a leader needs to know
 * what runs before them, so the team scope is a convenience, not a fence.
 * The picker lists every team because an organizer is asked „kdy
 * startuje Luckies?" all day, and this is the page that prints.
 */
export function StartListViewPage() {
  const { id } = useParams();
  const compId = id ?? "";
  const [params, setParams] = useSearchParams();
  const options = parseExportOptions(params);

  // A leader belongs to one team in practice; the first is theirs.
  const { data: myTeams = [] } = useMyTeams();
  const myTeam = myTeams[0];

  const set = (patch: Partial<ExportOptions>) =>
    setParams(exportSearchParams({ ...options, ...patch }), { replace: true });

  // Rows are narrowed server-side, so the screen, the print and the
  // downloads are literally one document; columns are this renderer's.
  const { data: doc, isLoading, error, refetch } = useStartList(
    compId,
    rowFilterQuery(options),
  );

  // The unfiltered document names every team on the list, so the picker
  // needs no endpoint of its own — and keeps its options while a filter
  // is active, which the filtered response could not.
  const { data: all } = useStartList(compId);
  const teams = useMemo(() => {
    // Keyed by id, and carrying the country so a foreign club is told
    // apart from a Czech one of the same name.
    const byID = new Map<ID, { id: ID; name: string; country?: string }>();
    for (const item of startListBlocks(all)) {
      for (const entry of item.entries ?? []) {
        byID.set(entry.team_id, {
          id: entry.team_id,
          name: entry.team_name,
          country: entry.team_country,
        });
      }
    }
    return [...byID.values()].sort((a, b) => a.name.localeCompare(b.name, "cs"));
  }, [all]);
  const mine = myTeam && teams.find((t) => t.id === myTeam.id);
  const filteredTeamName = teams.find((t) => t.id === options.team)?.name;
  // A link naming a club that does not start here (withdrew, stale link):
  // say so, instead of a picker reading „Všechny týmy" over an empty cut.
  const unknownTeam = !!options.team && !!all && !filteredTeamName;

  if (isLoading) {
    return (
      <div className={styles.page}>
        <LoadingState />
      </div>
    );
  }
  if (error || !doc) {
    return (
      <div className={styles.page}>
        <ErrorState
          title="Startovní listina není k dispozici"
          error={error}
          onRetry={refetch}
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.controls}>
        <header className={styles.header}>
          <h1>{doc.name}</h1>
          <Text variant="muted">
            Celkem <strong>{doc.entry_count}</strong>{" "}
            {pluralizeCzech(doc.entry_count, ["start", "starty", "startů"])} v{" "}
            <strong>{doc.category_count}</strong>{" "}
            {pluralizeCzech(doc.category_count, ["kategorii", "kategoriích", "kategoriích"])}
          </Text>
        </header>

        {doc.missing_time_limits > 0 && (
          <Alert variant="warn">
            Některé položky nemají v pravidlech časový limit, takže časy pod nimi jsou jen
            odhad.
          </Alert>
        )}

        <FilterPanel>
          <FilterField>
            <Select
              label="Tým"
              value={options.team ? String(options.team) : ""}
              onChange={(e) => set({ team: e.target.value || undefined })}
            >
              <option value="">Všechny týmy</option>
              {unknownTeam && <option value={String(options.team)}>Tým mimo listinu</option>}
              {/* The viewer's own club first: it is the one they came for. */}
              {mine && <option value={String(mine.id)}>{mine.name} (náš tým)</option>}
              {teams
                .filter((t) => t.id !== mine?.id)
                .map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.country ? `${t.name} (${t.country})` : t.name}
                  </option>
                ))}
            </Select>
          </FilterField>
          <FilterField>
            <Select
              label="Rozsah"
              value={options.scope}
              disabled={!options.team}
              onChange={(e) => set({ scope: e.target.value as ExportScope })}
            >
              {(Object.keys(EXPORT_SCOPE_LABELS) as ExportScope[]).map((s) => (
                <option key={s} value={s}>
                  {EXPORT_SCOPE_LABELS[s]}
                </option>
              ))}
            </Select>
          </FilterField>
          <FilterField action>
            <Button
              variant="primary"
              startIcon={<Printer size={16} aria-hidden="true" />}
              onClick={() => window.print()}
            >
              Tisk
            </Button>
          </FilterField>
        </FilterPanel>

        <fieldset className={styles.flags}>
          <legend>Zobrazit</legend>
          {EXPORT_FLAG_ORDER.map((f) => (
            <Checkbox
              key={f}
              label={EXPORT_FLAG_LABELS[f]}
              checked={options[f]}
              onChange={(e) => set({ [f]: e.target.checked })}
            />
          ))}
        </fieldset>

        {unknownTeam && (
          <Alert variant="warn">
            Vybraný tým na této listině nestartuje.{" "}
            <Button weight="ghost" size="sm" onClick={() => set({ team: undefined })}>
              Zobrazit celou listinu
            </Button>
          </Alert>
        )}
        {options.team && filteredTeamName && (
          <Alert variant="info">
            {options.scope === "categories" ? (
              <>
                Zobrazují se celé kategorie, ve kterých startuje <strong>{filteredTeamName}</strong>.
              </>
            ) : (
              <>
                Zobrazují se jen starty týmu <strong>{filteredTeamName}</strong>. Bloky zůstávají,
                aby bylo vidět, co běží mezi nimi.
              </>
            )}
          </Alert>
        )}
      </div>

      {/* Highlight the chosen club where it shares the page with others;
          in its own cut every row is its own, so mark the viewer's instead. */}
      <StartListSheet
        doc={doc}
        options={options}
        highlightTeam={
          options.team && options.scope === "categories" ? options.team : myTeam?.id
        }
      />

      {/* Only „zveřejněno" fixes the numbers; a návrh says the opposite on
          every day sheet above. */}
      {doc.published && (
        <footer className={styles.footer}>
          <Text variant="muted">
            Startovní čísla jsou pevná. Když se pořadí změní, start si své číslo nese s
            sebou a nikdo se nepřečíslovává.
          </Text>
        </footer>
      )}
    </div>
  );
}
